import { Box } from '@chakra-ui/react';
import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { z } from 'zod/v4';
import { useCreditFacilityOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionGuard } from '~/core/PermissionGuard';
import { PortfolioTemplate } from '~/packages/portfolio/ui/shared/PortfolioTemplate';
import { PortfolioSummaryContainer as PortfolioSummaryContainerV1 } from '~/packages/portfolio/ui/summary/PortfolioSummaryContainer';
import { PortfolioSummary } from '~/packages/portfolio/ui/summary/PortfolioSummaryV2';

// Types
const RouteParamsStruct = z.object({
  creditFacility: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const [queryParams] = useSearchParams();
  const { creditFacility: creditFacilityId } = RouteParamsStruct.parse(
    Object.fromEntries(queryParams),
  );
  const creditFacility = useCreditFacilityOrThrow(creditFacilityId);

  return (
    <PortfolioTemplate
      creditFacilityId={creditFacility.id}
      creditFacilityName={creditFacility.name}
      selectedTab="summary"
    >
      <PermissionGuard
        permissions={(context) => {
          return context.flags.displayLenderPortfolioMonitoringSummaryV2
            ? 'allow'
            : 'deny';
        }}
      >
        <PortfolioSummary creditFacility={creditFacility} />
      </PermissionGuard>

      <PermissionGuard
        permissions={(context) => {
          return context.flags.displayLenderPortfolioMonitoringSummaryV2
            ? 'deny'
            : 'allow';
        }}
      >
        <PortfolioSummaryContainerV1
          creditFacilityId={creditFacility.id}
          capitalProviderOrganizationIds={[user.organization.id]}
          dashboard="capitalProvider"
        />
      </PermissionGuard>
    </PortfolioTemplate>
  );
};

// Exports
export const Route = () => {
  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/portfolio/overview/facilities" />;
      }}
    >
      <Box width="100%" padding="2.5rem">
        <RouteBase />
      </Box>
    </RouteErrorBoundary>
  );
};
